export const organizationConfiguration = {
  title: 'Gestión de Organización',
  description: 'Gestiona la configuración de tu organización y etiquetas',
  tabs: {
    tags: 'Etiquetas',
  },
} as const

export const companySettings = {
  title: 'Configuración de Empresa',
  description: 'Gestiona la información y detalles de tu empresa',
  tabs: {
    websites: 'Sitios Web',
    competitors: 'Competidores',
    companyData: 'Datos de Empresa',
    brandIdentity: 'Identidad de Marca',
    brandVoices: 'Voces de Marca',
    linkedinProfiles: 'Perfiles de LinkedIn',
    clientProfile: 'Perfil de Cliente',
  },
  save: 'Guardar Cambios',
  updateSuccess: 'Datos de la empresa actualizados correctamente',
  updateError: 'Error al actualizar los datos de la empresa',
  errorLoading: 'Error al cargar los datos de la empresa',
  websites: {
    title: 'Sitios Web',
    description:
      'Gestiona los sitios web de tu empresa para el interlinking de contenidos. Un sitio por dominio o subdominio.',
    addWebsite: 'Agregar Sitio Web',
    editWebsite: 'Editar Sitio Web',
    deleteWebsite: 'Eliminar Sitio Web',
    name: 'Nombre',
    namePlaceholder: 'ej: Blog Principal',
    url: 'URL del Sitio',
    urlPlaceholder: 'ej: example.com',
    confirmDelete: '¿Estás seguro de que deseas eliminar este sitio web?',
    createSuccess: 'Sitio web creado correctamente',
    addError: 'No se pudo agregar el sitio web. Ingresá una URL válida.',
    domainConflict:
      'Ya tenés un sitio registrado para esta dirección. Los subdominios cuentan como sitios distintos.',
    indexedPages: '{{count}} páginas indexadas',
    indexedPages_one: '{{count}} página indexada',
    indexedPages_other: '{{count}} páginas indexadas',
    noIndexedPages: 'Sin páginas indexadas aún',
    unknownIndexedPages: 'Conteo de páginas no disponible',
    indexing: 'Indexando…',
    indexingFailed: 'Error al indexar',
    reindex: 'Reindexar',
    reindexStarted: 'Reindexado iniciado',
    reindexConfirm: {
      title: '¿Reindexar este sitio?',
      description: 'El reindexado reconstruye el índice con lo que encuentre esta corrida.',
      unknownPageCount: 'No pudimos verificar cuántas páginas tiene indexadas en este momento.',
      pageLossWarning:
        'Una página a la que el crawler no llegue esta vez queda marcada, pero una página que dos corridas seguidas no encuentren se borra del índice y deja de estar disponible para el interlinking: reindexar repetidamente con una cobertura inestable puede sacar páginas que siguen vivas.',
      confirm: 'Reindexar',
    },
    updateSuccess: 'Sitio web actualizado correctamente',
    updateError: 'No se pudo actualizar el sitio web. Ingresá una URL válida.',
    deleteSuccess: 'Sitio web eliminado correctamente',
    noWebsites: 'No hay sitios web configurados',
    noWebsitesDescription:
      'Agrega sitios web para habilitar el interlinking automático en tus contenidos',
    crawlConfig: {
      title: 'Configuración avanzada de crawl',
      description:
        'Controlá qué URLs se indexan para el interlinking. Un patrón por línea. Dejá ambos vacíos para crawlear todo el sitio. Guardar no dispara un crawl: los cambios se aplican al reindexar.',
      excludeLabel: 'Patrones de URL a excluir',
      excludePlaceholder: '/choose-your-number\n/tag',
      excludeHelp:
        'Omite las URLs que coincidan con alguno. Usalo para dejar fuera del índice páginas programáticas o de bajo valor (ej: /choose-your-number).',
      includeLabel: 'Patrones de URL a incluir',
      includePlaceholder: '/blog\n/guias',
      includeHelp:
        'Si lo definís, solo se indexan las URLs que coincidan con al menos un patrón. Normalmente dejalo vacío: los excludes son más seguros, porque un include estricto puede impedir que el crawler llegue a las páginas.',
      patternsError: 'Hasta {{maxPatterns}} patrones, cada uno de máximo {{maxLength}} caracteres.',
    },
  },
  competitors: {
    title: 'Competidores',
    description: 'Gestiona los competidores de tu organización',
    addCompetitor: 'Agregar Competidor',
    editCompetitor: 'Editar Competidor',
    deleteCompetitor: 'Eliminar Competidor',
    name: 'Nombre',
    namePlaceholder: 'ej: Competidor S.A.',
    url: 'URL del Sitio',
    urlPlaceholder: 'ej: competidor.com',
    confirmDelete: '¿Estás seguro de que deseas eliminar este competidor?',
    createSuccess: 'Competidor creado correctamente',
    addError: 'No se pudo agregar el competidor. Ingresá una URL válida.',
    updateSuccess: 'Competidor actualizado correctamente',
    updateError: 'No se pudo actualizar el competidor. Ingresá una URL válida.',
    deleteSuccess: 'Competidor eliminado correctamente',
    noCompetitors: 'No hay competidores configurados',
    noCompetitorsDescription:
      'Agregá competidores para evitar que sean mencionados en los contenidos generados',
  },
  brandTokens: {
    title: 'Identidad de marca',
    description:
      'Términos que identifican tu marca en búsquedas. Apretá Enter para agregar cada token. Si está vacío, los derivamos automáticamente del nombre de la organización.',
    label: 'Tokens de marca',
    placeholder: 'Escribí un token y apretá Enter',
    addError: 'Los tokens deben tener entre 1 y 60 caracteres y ser únicos.',
    remove: 'Quitar',
    autoDerivedHint: 'Sin tokens configurados — se derivan automáticamente de "{{name}}".',
  },
  productExclusions: {
    label: 'Lo que tu producto NO hace',
    placeholder:
      'Ej: no emitimos factura electrónica DIAN, no atendemos enterprise (+500 empleados), no servimos al rubro automotriz.',
  },
  linkedinProfiles: {
    title: 'Perfiles de LinkedIn',
    description: 'Agrega perfiles de LinkedIn para vincular posts en tus planes de contenido',
    addProfile: 'Agregar Perfil',
    deleteProfile: 'Eliminar Perfil',
    url: 'URL de LinkedIn',
    urlPlaceholder: 'ej: linkedin.com/in/johndoe',
    type: 'Tipo',
    typePersonal: 'Personal',
    typeCompany: 'Empresa',
    confirmDelete: '¿Estás seguro de que deseas eliminar este perfil de LinkedIn?',
    createSuccess: 'Perfil de LinkedIn agregado correctamente',
    addError: 'No se pudo agregar el perfil. Ingresá una URL de LinkedIn válida.',
    deleteSuccess: 'Perfil de LinkedIn eliminado correctamente',
    fetchPosts: 'Traer Posts',
    fetchPostsSuccess: '{{count}} posts de LinkedIn obtenidos',
    noProfiles: 'No hay perfiles de LinkedIn configurados',
    noProfilesDescription:
      'Agrega perfiles de LinkedIn para vincular automáticamente posts relevantes en tus planes de contenido',
  },
  clientProfile: {
    title: 'Perfil de Cliente',
    description:
      'Configuración operativa del pipeline de blog para esta organización. Los valores no definidos usan los de la organización o los valores por defecto del sistema.',
    sections: {
      voice: 'Voz y registro',
      branding: 'Branding y frescura',
      advanced: 'Publicación y avanzado',
    },
    fields: {
      register: {
        label: 'Registro',
        help: 'Trato gramatical usado en los textos generados.',
      },
      brandSuffix: {
        label: 'Sufijo de marca',
        placeholder: 'ej: | Nombre de Marca',
        help: 'Texto que se agrega al final de los títulos.',
      },
      emojiRule: {
        label: 'Emojis',
        help: 'Si se conservan o se eliminan los emojis del contenido.',
      },
      freshnessWindowDays: {
        label: 'Ventana de frescura (días)',
        help: 'Cantidad de días durante los cuales el contenido se considera fresco (1 a 365).',
      },
      yearRefreshMode: {
        label: 'Modo de actualización de año',
        help: 'Cómo se actualizan las referencias de año en el contenido.',
      },
      forbiddenTerms: {
        label: 'Términos prohibidos',
        placeholder: 'Escribí un término y apretá Enter',
        help: 'Términos que no deben aparecer en el contenido generado.',
      },
      mandatoryCapitalizations: {
        label: 'Capitalizaciones obligatorias',
        placeholder: 'Escribí un término y apretá Enter',
        help: 'Términos que siempre deben respetar estas mayúsculas/minúsculas.',
      },
      locationCode: {
        label: 'País',
      },
      languageCode: {
        label: 'Código de idioma',
      },
      market: {
        label: 'Mercado / País',
        placeholder: 'Seleccioná un país',
        help: 'País de destino para la investigación de keywords. Define el mercado y el idioma.',
      },
      wordpressMetaPlugin: {
        label: 'Plugin de metadatos de WordPress',
        help: 'Plugin de SEO usado para escribir los metadatos en WordPress.',
      },
      brandTerms: {
        label: 'Términos de marca',
        placeholder: 'Escribí un término y apretá Enter',
        help: 'Términos que identifican tu marca. Se guardan a nivel organización.',
      },
      valueProp: {
        label: 'Propuesta de valor',
        placeholder: 'ej: La forma más simple de gestionar tu SEO',
        help: 'Propuesta de valor de la marca. Se guarda a nivel organización.',
      },
      notes: {
        label: 'Notas',
        placeholder: 'Notas internas sobre esta configuración',
      },
      publishRouting: {
        label: 'Ruteo de publicación',
        help: 'Define dónde se publica el contenido (nunca cómo se autentica).',
        method: 'Método de publicación',
        none: 'Sin ruteo',
        authorId: 'ID de autor',
        authorIdPlaceholder: 'ej: 12',
        categoryIdsByLang: 'IDs de categoría por idioma',
        categoryLangLabel: 'Idioma',
        categoryIdsLabel: 'IDs (separados por coma)',
        addCategory: 'Agregar idioma',
        framerCollections: 'Colecciones de Framer',
        framerMarketLabel: 'Mercado',
        framerCollectionLabel: 'ID de colección',
        addFramerCollection: 'Agregar colección',
        removeEntry: 'Quitar',
        notes: 'Notas de ruteo',
      },
    },
    options: {
      register: {
        voseo: 'Voseo',
        tuteo: 'Tuteo',
        usted: 'Usted',
        neutro: 'Neutro',
      },
      emojiRule: {
        keep: 'Conservar',
        strip: 'Eliminar',
      },
      yearRefreshMode: {
        ask: 'Preguntar',
        atomic_all: 'Actualizar todo',
        atomic_skip: 'Omitir',
      },
      wordpressMetaPlugin: {
        rankmath: 'Rank Math',
        yoast: 'Yoast',
        none: 'Ninguno',
      },
      publishMethod: {
        'wordpress-apply': 'WordPress',
        'mcp-native': 'MCP nativo',
        'framer-api': 'API de Framer',
      },
      useDefault: 'Usar valor por defecto',
    },
    effectiveConfig: {
      title: 'Configuración efectiva',
      description:
        'Valores resultantes tras aplicar la precedencia: perfil sobre organización sobre valores por defecto.',
      sourceLabel: 'Origen',
      sources: {
        request: 'Solicitud',
        profile: 'Perfil',
        organization: 'Organización',
        default: 'Por defecto',
      },
    },
    actions: {
      save: 'Guardar Perfil',
      saving: 'Guardando...',
      clear: 'Limpiar Perfil',
    },
    form: {
      freshnessWindowRange: 'La ventana de frescura debe estar entre 1 y 365 días.',
      locationCodePositive: 'El código de ubicación debe ser un entero positivo.',
      languageCodeLength: 'El código de idioma debe tener entre 2 y 5 caracteres.',
      authorIdPositive: 'El ID de autor debe ser un entero positivo.',
      framerCollectionsRequired:
        'Las colecciones de Framer son obligatorias cuando el método es API de Framer.',
    },
    toast: {
      saveSuccess: 'Perfil de cliente guardado correctamente',
      saveError: 'No se pudo guardar el perfil de cliente: {{message}}',
      clearSuccess: 'Perfil de cliente eliminado correctamente',
      clearError: 'No se pudo eliminar el perfil de cliente: {{message}}',
    },
    states: {
      loading: 'Cargando el perfil de cliente...',
      error: 'Error al cargar el perfil de cliente',
      empty: 'Todavía no hay un perfil de cliente configurado',
      emptyDescription:
        'Configurá el perfil de cliente para personalizar el pipeline de blog de esta organización.',
      readOnly: 'Solo los administradores de la organización pueden editar el perfil de cliente.',
    },
    tags: {
      remove: 'Quitar',
    },
    confirmClear: {
      title: '¿Limpiar el perfil de cliente?',
      description:
        'Se eliminará el perfil de cliente de esta organización y la configuración volverá a los valores de la organización o del sistema. Esta acción no se puede deshacer.',
      confirm: 'Limpiar Perfil',
      cancel: 'Cancelar',
    },
    comingSoon: 'El editor del perfil de cliente estará disponible próximamente.',
  },
} as const
